// Parse a Post into a list of per-platform failure rows for the UI's
// failure-reason popup. Pulls from two sources, in order of fidelity:
//
//   1. `post.postingResults.failures` — the structured per-platform
//      result the worker writes in socialPostingProcessor. Each entry is
//      `{ accountId, provider, error }`. Preferred when present because
//      it survives lastError truncation and won't misparse colons in
//      error messages.
//
//   2. `post.lastError` — the legacy single-string field, shape
//      `linkedin: <msg>; facebook: <msg>; pinterest: <msg>`. We split on
//      `; ` first, then on the FIRST `:` (errors themselves can contain
//      colons, e.g. LinkedIn's `123:foo:bar`). Falls back to a single
//      "Error" row when the string isn't platform-prefixed (this is
//      what email-failed and image-generation-failed look like — they
//      come from non-publish paths and don't carry a platform prefix).
//
// `partially-posted` posts ALSO have `postingResults.success` — the
// popup uses that to show what DID work alongside what failed, so the
// user can see at a glance "LinkedIn + Facebook went out, Pinterest
// didn't" instead of just the failures.

const KNOWN_PLATFORMS = new Set([
  'linkedin', 'facebook', 'instagram', 'twitter', 'pinterest', 'youtube',
  'tiktok', 'reddit', 'telegram', 'discord', 'snapchat', 'whatsapp', 'google',
]);

const STATUS_LABEL = {
  'email-failed':            'Approval email send',
  'image-generation-failed': 'Image generation',
};

export function parsePostFailures(post) {
  if (!post) return [];
  const fromResults = Array.isArray(post.postingResults?.failures)
    ? post.postingResults.failures.map((f) => ({
        platform: String(f.provider || '').toLowerCase(),
        message: String(f.error || '').trim() || 'Unknown error',
      }))
    : null;
  if (fromResults && fromResults.length) return fromResults;

  const raw = String(post.lastError || '').trim();
  if (!raw) return [];

  // Email- / image-generation-failed paths emit a non-prefixed message,
  // so anchor the fallback to the queueStatus instead of pretending it
  // came from a platform.
  const status = String(post.queueStatus || '');
  if (STATUS_LABEL[status]) {
    return [{ platform: status, label: STATUS_LABEL[status], message: raw }];
  }

  return raw.split(/;\s*/).filter(Boolean).map((chunk) => {
    const i = chunk.indexOf(':');
    if (i === -1) return { platform: 'unknown', message: chunk.trim() };
    const head = chunk.slice(0, i).trim().toLowerCase();
    const tail = chunk.slice(i + 1).trim();
    if (KNOWN_PLATFORMS.has(head)) {
      // Worker uses "<provider>: <provider>: <inner>" sometimes (the
      // adapter prefixes its own error with the provider name, then
      // the aggregator prefixes it again). Strip the redundant inner
      // prefix so the popup reads cleanly.
      const innerPrefix = `${head}:`;
      const cleaned = tail.toLowerCase().startsWith(innerPrefix)
        ? tail.slice(innerPrefix.length).trim()
        : tail;
      return { platform: head, message: cleaned };
    }
    return { platform: 'unknown', message: chunk.trim() };
  });
}

// Same shape as parsePostFailures, for the platforms that DID publish.
// Empty for fully-failed posts; populated for partially-posted.
export function parsePostSuccesses(post) {
  if (!post) return [];
  return Array.isArray(post.postingResults?.success)
    ? post.postingResults.success.map((s) => ({
        platform: String(s.provider || '').toLowerCase(),
        url: s.url || null,
        externalPostId: s.postId || null,
      }))
    : [];
}

// Platforms the worker SKIPPED before any API call (e.g. a video-only platform
// like YouTube/Snapchat/TikTok targeted by a text/image post). These never
// appear in success OR failures, so without surfacing them they look like the
// post "didn't even try" — this exposes the recorded reason instead.
export function parsePostSkipped(post) {
  if (!post) return [];
  return Array.isArray(post.postingResults?.skipped)
    ? post.postingResults.skipped.map((s) => ({
        platform: String(s.provider || '').toLowerCase(),
        message: String(s.reason || '').trim() || 'Skipped',
      }))
    : [];
}

// One-shot predicate for the StatusBadge: should this badge be
// clickable to surface failure details?
export const FAILED_QUEUE_STATUSES = new Set([
  'posting-failed', 'partially-posted', 'email-failed', 'image-generation-failed',
]);

export function hasFailureDetails(post) {
  if (!post) return false;
  if (!FAILED_QUEUE_STATUSES.has(String(post.queueStatus || ''))) return false;
  return parsePostFailures(post).length > 0 || parsePostSkipped(post).length > 0;
}
